/**
 * OTP send rate-limit handling for the email-OTP auth flow.
 *
 * Supabase enforces two distinct server-side limits on `POST /otp`, both of
 * which reach the browser as HTTP 429:
 *   1. A short per-email resend interval (default ~60s) — the message carries
 *      the exact seconds remaining ("…you can only request this after N
 *      seconds.").
 *   2. A project-wide hourly email cap (`over_email_send_rate_limit`) — needs a
 *      much longer wait, with no per-second countdown.
 *
 * Production note (2026-06-03): the hosted GoTrue version tags the short
 * per-email cooldown with error_code `over_email_send_rate_limit` AND an
 * "after N seconds" message — i.e. the SAME code as the hourly cap. So we
 * classify seconds-first: any parseable "after N seconds" is a short cooldown
 * of exactly N, regardless of code; only an email-rate-limit signal WITHOUT a
 * parseable countdown is treated as the (long) hourly cap. Misclassifying the
 * short cooldown as the cap would lock a user out far longer than necessary.
 *
 * The cooldown is persisted in localStorage as an ABSOLUTE expiry timestamp so
 * a page reload or a back-to-email round-trip can't reset it — the volatile
 * React countdown it backs up was exactly how an impatient user re-fired into
 * the server limit (see docs/runbooks/2026-05-18-auth-supabase-rebranding.md).
 */

export type OtpSendOutcome =
  | { kind: 'cooldown'; seconds: number }
  | { kind: 'hourly_cap'; seconds: number }
  | { kind: 'invalid_email' }
  | { kind: 'generic' };

/** Fallback short cooldown when the server didn't give us a parseable count. */
export const DEFAULT_COOLDOWN_SECONDS = 60;

/**
 * Client-side lock applied on the hourly cap. The real server wait can be up to
 * an hour, but a 60-minute client lock risks stranding a legitimate user on a
 * false positive — so we use a forgiving 5-minute lock that breaks the
 * spam-resend loop and re-surfaces the cap message if they retry too soon. The
 * server remains the real enforcement.
 */
export const HOURLY_CAP_COOLDOWN_SECONDS = 5 * 60;

/** Clamp returned/stored cooldowns so a corrupt entry can't lock a user out. */
export const MAX_COOLDOWN_SECONDS = 60 * 60;

const STORAGE_PREFIX = 'og:otp:cooldown:';

const RETRY_AFTER_RE = /after (\d+)\s*seconds?/i;
const EMAIL_RATE_LIMIT_RE = /email rate limit/i;

/** Pull the "after N seconds" countdown out of a Supabase rate-limit message. */
export function parseRetryAfterSeconds(message: string | null | undefined): number | null {
  if (!message) return null;
  const match = message.match(RETRY_AFTER_RE);
  if (!match) return null;
  const seconds = Number.parseInt(match[1], 10);
  return Number.isFinite(seconds) ? seconds : null;
}

/**
 * Classify a failed `signInWithOtp` into a UX bucket. Seconds-first: a concrete
 * countdown always means the short per-email interval, even when the code says
 * `over_email_send_rate_limit`.
 */
export function classifyOtpSendError(input: {
  status?: number;
  code?: string;
  message?: string | null;
}): OtpSendOutcome {
  const seconds = parseRetryAfterSeconds(input.message);
  if (seconds !== null) {
    return { kind: 'cooldown', seconds: Math.min(seconds, MAX_COOLDOWN_SECONDS) };
  }

  const code = input.code ?? '';
  if (code === 'over_email_send_rate_limit' || EMAIL_RATE_LIMIT_RE.test(input.message ?? '')) {
    return { kind: 'hourly_cap', seconds: HOURLY_CAP_COOLDOWN_SECONDS };
  }

  // GoTrue rejects undeliverable addresses (e.g. domains without MX records)
  // with 400 `email_address_invalid` — a user-fixable input problem, not a
  // connection problem; don't lump it into the generic copy.
  if (code === 'email_address_invalid') {
    return { kind: 'invalid_email' };
  }

  return { kind: 'generic' };
}

/** Render a remaining-seconds count as `m:ss` for the countdown UI. */
export function formatCooldown(seconds: number): string {
  const total = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(total / 60);
  return `${minutes}:${String(total % 60).padStart(2, '0')}`;
}

function keyFor(email: string): string {
  return STORAGE_PREFIX + email.trim().toLowerCase();
}

/** Persist a cooldown for `email` lasting `seconds` from `nowMs`. */
export function setOtpCooldown(email: string, seconds: number, nowMs: number = Date.now()): void {
  const clamped = Math.min(Math.max(0, seconds), MAX_COOLDOWN_SECONDS);
  try {
    localStorage.setItem(keyFor(email), String(nowMs + clamped * 1000));
  } catch {
    // localStorage unavailable (private mode / blocked) — degrade to no
    // persisted cooldown rather than break the auth form.
  }
}

/** Seconds remaining on `email`'s cooldown (0 if none/expired), clamped. */
export function getOtpCooldownRemaining(email: string, nowMs: number = Date.now()): number {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(keyFor(email));
  } catch {
    return 0;
  }
  if (!raw) return 0;
  const expiry = Number.parseInt(raw, 10);
  if (!Number.isFinite(expiry)) return 0;
  const remainingMs = expiry - nowMs;
  if (remainingMs <= 0) return 0;
  return Math.min(Math.ceil(remainingMs / 1000), MAX_COOLDOWN_SECONDS);
}

/** Drop `email`'s cooldown (e.g. after a successful verification). */
export function clearOtpCooldown(email: string): void {
  try {
    localStorage.removeItem(keyFor(email));
  } catch {
    // ignore — see setOtpCooldown
  }
}
